/**
 * カスタムフォント hooks (Phase 29.4)
 * - useCustomFonts: 一覧取得 + @font-face を DOM に動的注入
 * - useUploadCustomFont: Storage へ .ttf/.otf/.woff/.woff2 をアップロード + Firestore へ登録
 * - useDeleteCustomFont: Firestore + Storage から削除
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { useEffect } from 'react';

import { db, storage } from '@/lib/firebase';
import { safeAddDoc } from '@/lib/safeWrite';

import type { CustomFont } from '@goenhub/shared';
import {
  CUSTOM_FONT_MAX_BYTES,
  CUSTOM_FONT_STORAGE_PATH,
  inferCustomFontFormat,
} from '@goenhub/shared';

export type CustomFontRecord = CustomFont;

const FONT_STYLE_ID = 'custom-fonts-style';

function injectFontFaces(fonts: CustomFont[]): void {
  let style = document.getElementById(FONT_STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = FONT_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = fonts
    .map(
      (f) =>
        `@font-face { font-family: "${f.family}"; src: url("${f.url}") format("${f.format}"); font-display: swap; }`,
    )
    .join('\n');
}

export function useCustomFonts() {
  const queryResult = useQuery({
    queryKey: ['customFonts'],
    queryFn: async (): Promise<CustomFontRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'customFonts'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => {
        const data = d.data() as CustomFont;
        return { ...data, id: d.id };
      });
    },
  });

  useEffect(() => {
    if (queryResult.data) {
      injectFontFaces(queryResult.data);
    }
  }, [queryResult.data]);

  return queryResult;
}

export function useUploadCustomFont() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { file: File; name: string; family: string }) => {
      const { file, name, family } = params;
      if (file.size > CUSTOM_FONT_MAX_BYTES) {
        throw new Error(`ファイルサイズが大きすぎます（最大 ${CUSTOM_FONT_MAX_BYTES / 1024 / 1024}MB）`);
      }
      const format = inferCustomFontFormat(file.name);
      if (!format) {
        throw new Error('対応しているのは .ttf / .otf / .woff / .woff2 のみです');
      }
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${CUSTOM_FONT_STORAGE_PATH}/${Date.now()}-${safeName}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file, { contentType: 'font/' + format });
      const url = await getDownloadURL(ref);
      await safeAddDoc(collection(db, 'customFonts'), {
        name,
        family,
        url,
        storagePath: path,
        format,
        size: file.size,
        createdAt: serverTimestamp(),
      });
      await qc.invalidateQueries({ queryKey: ['customFonts'] });
    },
  });
}

export function useDeleteCustomFont() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (font: CustomFontRecord) => {
      try {
        await deleteObject(storageRef(storage, font.storagePath));
      } catch (e) {
        console.warn('[useDeleteCustomFont] Storage delete failed (continue):', e);
      }
      await deleteDoc(doc(db, 'customFonts', font.id));
      await qc.invalidateQueries({ queryKey: ['customFonts'] });
    },
  });
}
